import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Auctions_dropdown from "../dropdown/Auctions_dropdown";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import { fetchGetAllNftPaginate, stateCampaignNft } from "../../redux/apiSlice";
import { useAuth } from "../../contexts/auth";
export const baseURL = `${process.env.NEXT_PUBLIC_BASE_URL}/`;

const CategoryItem = () => {
  const { campaignNft, isLoading, filterStatus } = useSelector((state) => state.api);
  const dispatch = useDispatch();
  const router = useRouter();
  const { user } = useAuth();
  const PAGE_SIZE = 12;
  const total = useMemo(() => Number(campaignNft?.count || 0), [campaignNft]);
  const [page, setPage] = useState(1);

  // Map filter status to API parameters
  const getApiParams = (status) => {
    switch (status) {
      case "Sold":
        return { All: false, isSold: true };
      case "Available":
        return { All: false, isSold: false };
      case "All":
      default:
        return { All: true, isSold: false };
    }
  };

  useEffect(() => {
    const fetchData = () => {
      if (router.isReady) {
        const { campaignId } = router.query;
        if (campaignId === undefined) return null;
        // Reset to first page when campaign or filter changes
        setPage(1);
        const apiParams = getApiParams(filterStatus);
        try {
          dispatch(
            fetchGetAllNftPaginate({
              id: campaignId,
              page: 1,
              ...apiParams,
            }),
          );
        } catch (error) {
          console.error("API request failed:", error);
        }
      }
    };

    fetchData();
  }, [router.isReady, dispatch, router.query, filterStatus]);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / PAGE_SIZE) : 1),
    [total],
  );

  const goToPage = (newPage) => {
    if (!router.isReady) return;
    const { campaignId } = router.query;
    if (!campaignId) return;
    const safePage = Math.min(Math.max(newPage, 1), totalPages);
    setPage(safePage);
    const apiParams = getApiParams(filterStatus);
    dispatch(
      fetchGetAllNftPaginate({
        id: campaignId,
        page: safePage,
        ...apiParams,
      }),
    );
  };

  const handlePrev = () => goToPage(page - 1);
  const handleNext = () => goToPage(page + 1);

  const showingFrom = useMemo(
    () => (total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1),
    [page, total],
  );
  const showingTo = useMemo(
    () => Math.min(page * PAGE_SIZE, total || 0),
    [page, total],
  );

  return (
    <div>
      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 px-6">
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          campaignNft?.campaigns?.map((item, index) => {
            // Ownership: a sold piece belongs to its buyer (owner.ownerId);
            // an unsold piece still belongs to its creator. Never offer BUY
            // on the viewer's own art, on sold pieces, or on pieces that
            // aren't currently listed for sale.
            const uid = user?.id;
            const ownedByViewer = Boolean(
              uid &&
                (item.owner?.ownerId
                  ? item.owner.ownerId === uid
                  : item.creator?.creatorId === uid),
            );
            const buyable =
              !ownedByViewer && !item.isSold && item.isListed !== false;
            const stateLabel = ownedByViewer
              ? "Yours"
              : item.isSold
                ? "Sold"
                : "Not for sale";
            return (
              <Link
                href={`/marketplace/${item._id}`}
                className="block group"
                key={index}
              >
                <article className="group relative">
                  <div className="bg-white dark:bg-jacarta-700 rounded-lg border border-jacarta-100 dark:border-jacarta-600 transition-all duration-300 hover:shadow-xl hover:border-accent/50 dark:hover:border-accent/50 overflow-hidden">
                    {/* Image Container */}
                    <figure className="relative overflow-hidden bg-jacarta-50 dark:bg-jacarta-600 aspect-[5/4]">
                      <Image
                        width={300}
                        height={240}
                        src={baseURL + item.imageUrl}
                        alt={item.itemName || "Impact Token"}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                        priority={index < 4}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </figure>

                    {/* Content Section */}
                    <div className="p-5 space-y-4">
                      {/* Title */}
                      <h3 className="font-semibold text-lg text-jacarta-700 dark:text-white group-hover:text-accent transition-colors duration-200 line-clamp-2 leading-relaxed">
                        {item.itemName}
                      </h3>

                      {/* Creator Badge */}
                      {item.creator && (
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">
                              {item.creator.username?.charAt(0).toUpperCase() ||
                                "U"}
                            </span>
                          </div>
                          <p
                            className="text-sm text-jacarta-500 dark:text-jacarta-400 truncate flex-1"
                            title={item.creator.username}
                          >
                            {item.creator.username || "Unknown Creator"}
                          </p>
                        </div>
                      )}

                      {/* Price and Action */}
                      <div className="flex items-center justify-between pt-3 border-t border-jacarta-100 dark:border-jacarta-600">
                        <div>
                          <p className="text-xs text-jacarta-500 dark:text-jacarta-400 mb-1.5">
                            Price
                          </p>
                          <div className="text-lg font-bold text-accent">
                            {item.currency === "ETH"
                              ? `${item.price || item.priceEth || 0} ETH`
                              : `$${item.price || item.priceUsd || 0}`}
                          </div>
                        </div>
                        {buyable ? (
                          <button
                            className="bg-accent hover:bg-accent-dark text-white font-medium py-2.5 px-5 rounded-lg transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent/50"
                            onClick={(e) => {
                              // The real checkout lives on the detail page —
                              // the old buy modal was an empty shell with no
                              // purchase action.
                              e.preventDefault();
                              e.stopPropagation();
                              router.push(`/marketplace/${item._id}`);
                            }}
                          >
                            <span className="text-sm">BUY NOW</span>
                          </button>
                        ) : (
                          <span className="px-4 py-2 text-sm font-semibold rounded-lg bg-jacarta-100 text-jacarta-500 dark:bg-jacarta-600 dark:text-jacarta-300">
                            {stateLabel}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Hidden dropdown for compatibility */}
                    <div className="hidden">
                      <Auctions_dropdown
                        share_to={`/marketplace/${item._id}`}
                        classes="dark:hover:bg-jacarta-600 dropup hover:bg-jacarta-100 rounded-full"
                      />
                    </div>
                  </div>
                </article>
              </Link>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {!isLoading && total > PAGE_SIZE && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 px-6">
          <div className="text-sm text-jacarta-500 dark:text-jacarta-400">
            Showing {showingFrom}-{showingTo} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={page <= 1}
              className={`px-3 py-2 rounded-lg border border-jacarta-200 dark:border-jacarta-600 text-sm ${
                page <= 1
                  ? "text-jacarta-300 dark:text-jacarta-500 cursor-not-allowed"
                  : "text-jacarta-700 dark:text-white hover:border-accent hover:text-accent"
              }`}
            >
              Prev
            </button>
            <span className="text-sm text-jacarta-500 dark:text-jacarta-400">
              Page {page} / {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={page >= totalPages}
              className={`px-3 py-2 rounded-lg border border-jacarta-200 dark:border-jacarta-600 text-sm ${
                page >= totalPages
                  ? "text-jacarta-300 dark:text-jacarta-500 cursor-not-allowed"
                  : "text-jacarta-700 dark:text-white hover:border-accent hover:text-accent"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryItem;
